

export class Metadata {
    storageLimit: string;
}

export class Plan {
    id: string;
    amount: number;
    metadata: Metadata;
    currency: string;
    interval: string;
    product?: any;
    productId?: any;
    active: boolean;
    status: string;
}

export class BillingDetails {
    percentageUsed: number;
    currentUserUsage: number;
    storageLimit: number;
    plan: Plan;
    subscriptionId: string;
    customer: any;
    invoice: Invoice;
}

export class BillingModel {
    userId: string;
    billingId: string;
}

export class Invoice {
    id: string;
    object: string;
    account_country: string;
    account_name?: any;
    amount_due: number;
    amount_paid: number;
    amount_remaining: number;
    application_fee_amount?: any;
    attempt_count: number;
    attempted: boolean;
    auto_advance: boolean;
    billing: string;
    billing_reason: string;
    charge?: any;
    collection_method: string;
    created: number;
    currency: string;
    custom_fields?: any;
    customer: string;
    customer_address?: any;
    customer_email: string;
    customer_name?: any;
    customer_phone?: any;
    customer_shipping?: any;
    customer_tax_exempt: string;
    customer_tax_ids: any[];
    default_payment_method?: any;
    default_source?: any;
    default_tax_rates: any[];
    description?: any;
    discount?: any;
    due_date?: any;
    ending_balance: number;
    footer?: any;
    hosted_invoice_url: string;
    invoice_pdf: string;
    lines: Lines;
    livemode: boolean;
    next_payment_attempt?: any;
    number: string;
    paid: boolean;
    payment_intent: PaymentIntent;
    period_end: number;
    period_start: number;
    post_payment_credit_notes_amount: number;
    pre_payment_credit_notes_amount: number;
    receipt_number?: any;
    starting_balance: number;
    statement_descriptor?: any;
    status: string;
    status_transitions: StatusTransitions;
    subscription: string;
    subscription_proration_date: number;
    subtotal: number;
    tax?: any;
    tax_percent?: any;
    threshold_reason?: any;
    total_tax_amounts: any[];
    transfer_data?: any;
    total: number;
    webhooks_delivered_at: number;
}

export interface Period {
    start: number;
    end: number;
}

export interface Datum {
    id: string;
    object: string;
    amount: number;
    currency: string;
    date: number;
    description: string;
    discountable: boolean;
    invoice_item?: any;
    livemode: boolean;
    metadata: Metadata;
    period: Period;
    plan: Plan;
    proration: boolean;
    quantity: number;
    subscription: string;
    subscription_item: string;
    tax_amounts: any[];
    tax_rates: any[];
    type: string;
    unified_proration: boolean;
}

export interface Lines {
    object: string;
    data: Datum[];
    has_more: boolean;
    url: string;
}

export interface Charges {
    object: string;
    data: any[];
    has_more: boolean;
    url: string;
}

export interface NextAction {
    redirect_to_url?: any;
    type: string;
}

export interface Card {
    request_three_d_secure: string;
}

export interface PaymentMethodOptions {
    card: Card;
}

export interface PaymentIntent {
    id: string;
    object: string;
    amount: number;
    amount_capturable: number;
    amount_received: number;
    application?: any;
    application_fee_amount?: any;
    canceled_at?: any;
    cancellation_reason?: any;
    capture_method: string;
    charges: Charges;
    client_secret: string;
    confirmation_method: string;
    created: number;
    currency: string;
    customer: string;
    description: string;
    invoice: string;
    last_payment_error?: any;
    livemode: boolean;
    next_action: NextAction;
    on_behalf_of?: any;
    payment_method: string;
    payment_method_options: PaymentMethodOptions;
    payment_method_types: string[];
    receipt_email?: any;
    review?: any;
    setup_future_usage: string;
    shipping?: any;
    source?: any;
    statement_descriptor?: any;
    statement_descriptor_suffix?: any;
    status: string;
    transfer_data?: any;
    transfer_group?: any;
    allowed_source_types?: any;
}

export interface StatusTransitions {
    finalized_at: number;
    marked_uncollectible_at?: any;
    paid_at?: any;
    voided_at?: any;
}

export interface Items {
    object: string;
    data: Datum[];
    has_more: boolean;
    url: string;
}

export interface LatestInvoice {
    id: string;
    object: string;
    account_country: string;
    account_name?: any;
    amount_due: number;
    amount_paid: number;
    amount_remaining: number;
    application_fee_amount?: any;
    attempt_count: number;
    attempted: boolean;
    auto_advance: boolean;
    billing: string;
    billing_reason: string;
    charge: string;
    collection_method: string;
    created: number;
    currency: string;
    custom_fields?: any;
    customer: string;
    customer_address?: any;
    customer_email: string;
    customer_name?: any;
    customer_phone?: any;
    customer_shipping?: any;
    customer_tax_exempt: string;
    customer_tax_ids: any[];
    default_payment_method?: any;
    default_source?: any;
    default_tax_rates: any[];
    description?: any;
    discount?: any;
    due_date?: any;
    ending_balance: number;
    footer?: any;
    hosted_invoice_url: string;
    invoice_pdf: string;
    lines: Lines;
    livemode: boolean;
    metadata: Metadata;
    next_payment_attempt?: any;
    number: string;
    paid: boolean;
    payment_intent: PaymentIntent;
    period_end: number;
    period_start: number;
    post_payment_credit_notes_amount: number;
    pre_payment_credit_notes_amount: number;
    receipt_number?: any;
    starting_balance: number;
    statement_descriptor?: any;
    status: string;
    status_transitions: StatusTransitions;
    subscription: string;
    subscription_proration_date: number;
    subtotal: number;
    tax?: any;
    tax_percent?: any;
    threshold_reason?: any;
    total_tax_amounts: any[];
    transfer_data?: any;
    total: number;
    webhooks_delivered_at: number;
}

export interface Subscription {
    id: string;
    object: string;
    application_fee_percent?: any;
    billing: string;
    billing_cycle_anchor: number;
    billing_thresholds?: any;
    cancel_at?: any;
    cancel_at_period_end: boolean;
    canceled_at?: any;
    collection_method: string;
    created: number;
    current_period_end: number;
    current_period_start: number;
    customer: string;
    days_until_due?: any;
    default_payment_method?: any;
    default_source?: any;
    default_tax_rates: any[];
    discount?: any;
    ended_at?: any;
    items: Items;
    latest_invoice: LatestInvoice;
    livemode: boolean;
    metadata: Metadata;
    pending_setup_intent?: any;
    plan: Plan;
    quantity: number;
    schedule?: any;
    start: number;
    start_date: number;
    status: string;
    tax_percent?: any;
    transfer_data?: any;
    trial_end?: any;
    trial_start?: any;
}




